'use client';

import React, { useEffect, useState, useRef } from 'react';
import styles from './scss/segmentation.module.scss';
import ApexBarChart from '../../components/custom_components/apexChartsTypes/barChart';
import ApexLineChart from '../../components/custom_components/apexChartsTypes/lineChart';
import { getLycaChurnPredictionProbability } from '../../apiservice/aiLycaPilot';
import DonutChart from '../../components/custom_components/apexChartsTypes/donutChart';

const TypingText = ({
  text,
  onDone,
  speed = 50,
}: {
  text: string;
  onDone?: () => void;
  speed?: number;
}) => {
  const [displayedText, setDisplayedText] = useState('');

  useEffect(() => {
    let i = 0;
    setDisplayedText('');
    const interval = setInterval(() => {
      setDisplayedText(text.slice(0, i + 1));
      i++;
      if (i === text.length) {
        clearInterval(interval);
        onDone?.();
      }
    }, speed);

    return () => clearInterval(interval);
  }, [text, speed, onDone]);

  return <>{displayedText}</>;
};

const sections = [
  {
    title: 'Churn Probability',
    desc: 'Identify customers most likely to leave and act early with insights that drive retention.',
    chart: 'bar',
    caption: 'Churn Probability',
    footer:
      'Focus on years with high growth to replicate strategies and investigate low-performing years to uncover issues.',
    color: '#575CFF',
  },
];

const Churn2 = ({ onComplete }: { onComplete?: () => void }) => {
  const [phase, setPhase] = useState<'title' | 'desc' | 'caption' | 'footer' | 'done'>('title');
  const [series, setSeries] = useState<any[]>([]);
  const [labels, setLabels] = useState<any[]>([]);

  // Refs for scrolling
  const titleRef = useRef<HTMLParagraphElement>(null);
  const descRef = useRef<HTMLSpanElement>(null);
  const captionRef = useRef<HTMLParagraphElement>(null);
  const footerRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    let el: HTMLElement | null = null;
    if (phase === 'title') el = titleRef.current;
    else if (phase === 'desc') el = descRef.current;
    else if (phase === 'caption') el = captionRef.current;
    else if (phase === 'footer') el = footerRef.current;

    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [phase]);

  // const handleDone = () => {
  //   if (phase === 'title') setPhase('desc');
  //   else if (phase === 'desc') setPhase('caption');
  //   else if (phase === 'caption') setPhase('footer');
  //   else if (phase === 'footer') {
  //     setPhase('done');
  //     onComplete?.();
  //   }
  // };

  const handleDone = () => {
    if (phase === 'title') {
      setPhase('desc');
    } else if (phase === 'desc') {
      if (!current.caption) {
        // ⬅️ Skip caption if it's empty
        setPhase('footer');
      } else {
        setPhase('caption');
      }
    } else if (phase === 'caption') {
      setPhase('footer');
    } else if (phase === 'footer') {
      setPhase('done');
      onComplete?.(); // notify parent to load next tab
    }
  };

  useEffect(() => {
    const getLycaChurnPredictionProbabilityData = async () => {
      try {
        const body = { fromYear: '2024', toYear: '2025', domainId: ' ' };
        const res = await getLycaChurnPredictionProbability(body);

        if (res?.data?.statusCode === 200) {
          const data = res.data.response;

          // Exclude "overall likely to churn"
          const filteredData = data.filter(
            (item: any) => item.segement.toLowerCase() !== 'overall likely to churn'
          );

          setSeries(filteredData.map((item: any) => Math.round(item.percentage)));
          setLabels(filteredData.map((item: any) => item.segement));
        }
      } catch (error) {
        console.error('Error fetching churn prediction probability data:', error);
      }
    };

    getLycaChurnPredictionProbabilityData();
  }, []);

  const current = sections[0];

  const renderChart = () => {
    switch (current.chart) {
      case 'bar':
        return (
          <ApexBarChart
            isStacked={false}
            series={[{ data: [44, 55, 41, 67, 22, 43] }]}
            colors={[current.color]}
            categories={['2019', '2020', '2021', '2022', '2023', '2024']}
          />
        );
      case 'donut':
        return (
          <DonutChart
            series={[44, 55, 41, 17, 15]}
            labels={['Team A', 'Team B', 'Team C', 'Team D']}
          />
        );
      default:
        return (
          <ApexLineChart
            series={[{ data: [44, 55, 41, 67, 22, 43] }]}
            categories={['2019', '2020', '2021', '2022', '2023', '2024']}
          />
        );
    }
  };

  return (
    <div className={styles.clvchartssec}>
      <div className={`${styles.clvchartwrapper} revealing`}>
        {/* HEADER */}
        <div className={styles.clvwrapheader}>
          <p className={styles.clvwrapheadertxt} ref={titleRef}>
            {phase === 'title' ? (
              <TypingText text={current.title} onDone={handleDone} />
            ) : (
              current.title
            )}
          </p>
          <span className={styles.clvwrpadesc} ref={descRef}>
            {phase === 'desc' ? (
              <TypingText text={current.desc} onDone={handleDone} />
            ) : (
              phase !== 'title' && current.desc
            )}
          </span>
        </div>

        {/* BODY */}
        {['caption', 'footer', 'done'].includes(phase) && (
          <div className={styles.clvwrapbody}>
            <p className={styles.clvwrapcaptions} ref={captionRef}>
              {phase === 'caption' ? (
                <TypingText text={current.caption} onDone={handleDone} />
              ) : (
                current.caption
              )}
            </p>
            {current.chart === 'bar' ? (
              // <ApexBarChart
              //   isStacked={false}
              //   series={series}
              //   colors={[current.color]}
              //   categories={labels}
              // />
              <DonutChart
              series={series}
              labels={labels}
          />
            ) : (
              <ApexLineChart
                series={[{ data: [44, 55, 41, 67, 22, 43] }]}
                categories={['2019', '2020', '2021', '2022', '2023', '2024']}
              />
            )}
          </div>

        )}


        {/* FOOTER */}
        {['footer', 'done'].includes(phase) && (
          <div className={styles.clvwrapfooter}>
            <span className="insightsbtn">AI Insights</span>
            <p className={styles.clvwrapfttxt} ref={footerRef}>
              {phase === 'footer' ? (
                <TypingText text={current.footer} onDone={handleDone} />
              ) : (
                current.footer
              )}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Churn2;
